import { zodResolver } from '@hookform/resolvers/zod'
import {
	type PostAuthSignInWithEmailData,
	postAuthSignInWithEmail
} from '@repo/api-portal-sdk/gen'
import { Button } from '@repo/ui/button'
import { Field, FieldGroup } from '@repo/ui/field'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { Spinner } from '@repo/ui/spinner'
import { useMutation } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { resolveAppLanguageCode, useTranslation } from '@/lib/i18n'
import { createLoginSchema } from '@/routes/-lib/auth-schemas'
import { AuthTextField } from './auth-text-field'

export function LoginForm(props: { onSuccess: () => Promise<void> }) {
	const { t, i18n } = useTranslation()
	const language = resolveAppLanguageCode(
		i18n.resolvedLanguage ?? i18n.language
	)
	const schema = createLoginSchema(t)
	const form = useForm<z.infer<ReturnType<typeof createLoginSchema>>>({
		resolver: zodResolver(schema),
		defaultValues: { email: '', password: '' }
	})
	const signIn = useMutation({
		mutationFn: (body: PostAuthSignInWithEmailData['body']) =>
			postAuthSignInWithEmail({ body }),
		onError: () => form.resetField('password'),
		onSuccess: props.onSuccess
	})

	return (
		<form
			onSubmit={form.handleSubmit((data) =>
				signIn.mutate({ ...data, rememberMe: false, language })
			)}
		>
			<FieldGroup className='gap-4'>
				{signIn.isError ? (
					<AuthErrorAlert
						error={signIn.error}
						onDismiss={signIn.reset}
					/>
				) : null}
				<AuthTextField
					autoComplete='email'
					control={form.control}
					label={t('common:auth.email')}
					name='email'
					placeholder={t('common:auth.email_placeholder')}
					type='email'
				/>
				<AuthTextField
					autoComplete='current-password'
					control={form.control}
					label={t('common:auth.password')}
					name='password'
					type='password'
				/>
				<div className='flex justify-end'>
					<Link
						className='text-muted-foreground text-xs hover:text-foreground'
						to='/forgot-password'
					>
						{t('common:auth.forgot_password')}
					</Link>
				</div>
				<Field>
					<Button disabled={signIn.isPending} size='lg' type='submit'>
						{signIn.isPending ? (
							<Spinner />
						) : (
							t('common:auth.login_submit')
						)}
					</Button>
				</Field>
			</FieldGroup>
		</form>
	)
}
